import { useMutation, useQuery } from '@tanstack/react-query';
import { Download, Upload } from 'lucide-react';
import { useState } from 'react';
import { apiRequest, downloadApiFile, getApiErrorMessage } from '../../lib/api';
import { loadOptions, todayInPakistan, type LocationOption } from './manualSalesOptions';

interface Preview {
  preview_token: string;
  valid_count: number;
  invalid_count: number;
  skipped_count: number;
  rows: Array<{
    row_number: number;
    status: string;
    detail: string;
    values: Record<string, string>;
  }>;
}

export function ManualSalesImportDialog({ campaignId, accessToken, workspaceId, startsOn, endsOn, onClose, onSaved }: {
  campaignId: string;
  accessToken: string;
  workspaceId: string;
  startsOn: string;
  endsOn: string;
  onClose: () => void;
  onSaved: (count: number) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [date, setDate] = useState(todayInPakistan);
  const [storeSearch, setStoreSearch] = useState('');
  const [selectedStores, setSelectedStores] = useState<Set<string>>(new Set());
  const url = `/sales/campaigns/${campaignId}/manual-import/?${new URLSearchParams({ workspace: workspaceId })}`;
  const locations = useQuery({
    queryKey: ['manual-sale-locations', campaignId, workspaceId],
    queryFn: ({ signal }) => loadOptions<LocationOption>(`/sales/campaigns/${campaignId}/manual-entry/options/?${new URLSearchParams({ workspace: workspaceId, kind: 'locations' })}`, accessToken, signal),
  });
  const visibleStores = (locations.data ?? []).filter((item) => `${item.name} ${item.city}`.toLowerCase().includes(storeSearch.trim().toLowerCase()));
  const selectedLocations = (locations.data ?? []).filter((item) => selectedStores.has(item.id));
  const validDate = Boolean(date && date >= startsOn && date <= endsOn && selectedLocations.every((item) => date <= item.today));
  const canDownload = locations.isSuccess && selectedStores.size > 0 && selectedStores.size <= 50 && validDate;
  const template = useMutation({
    mutationFn: () => {
      const params = new URLSearchParams({ sales_date: date });
      selectedStores.forEach((id) => params.append('location_ids', id));
      return downloadApiFile(`${url}&${params}`, { accessToken });
    },
  });
  const upload = useMutation({
    mutationFn: async (confirm: boolean) => {
      if (!file) throw new Error('Choose a CSV file.');
      const form = new FormData();
      form.set('file', file);
      form.set('reason', reason);
      form.set('mode', confirm ? 'confirm' : 'preview');
      if (confirm && preview) {
        form.set('preview_token', preview.preview_token);
        [...selected].sort((a, b) => a - b).forEach((row) => form.append('selected_rows', String(row)));
        const result = await apiRequest<{ imported_count: number; already_imported_count: number }>(url, { method: 'POST', accessToken, body: form });
        onSaved(result.imported_count + result.already_imported_count);
      } else {
        const result = await apiRequest<Preview>(url, { method: 'POST', accessToken, body: form });
        setPreview(result);
        setSelected(new Set(result.rows.filter((row) => row.status === 'valid').map((row) => row.row_number)));
      }
    },
  });
  function resetPreview() {
    setPreview(null);
    setSelected(new Set());
    upload.reset();
  }
  const validRows = preview?.rows.filter((row) => row.status === 'valid') ?? [];
  const canPreview = Boolean(file && reason.trim() && !upload.isPending);

  return <div className="confirmation-backdrop" role="presentation">
    <section aria-label="Import campaign sales CSV" aria-modal="true" className="confirmation-dialog manual-sales-dialog" role="dialog">
      <p className="eyebrow">Manual sales</p>
      <h2>Import campaign sales CSV</h2>
      <p>BA assignment is optional. Leave BA email and assignment fields blank to record unassigned campaign sales. Imports require normal sales review; they do not create attendance.</p>
      <p className="inline-help">Choose stores and a sales date, then download a prefilled template and enter quantities. Blank or zero quantities are skipped. Up to 200 rows / 1 MB. BFC generates references automatically; identical rows cannot be imported twice. Free samples are not imported as sales.</p>
      <fieldset disabled={upload.isPending} className="manual-sales-fields">
        <label>Template sales date<input type="date" min={startsOn} max={endsOn < todayInPakistan() ? endsOn : todayInPakistan()} value={date} onChange={(event) => { setDate(event.target.value); template.reset(); }} /></label>
        <fieldset className="form-field schedule-store-picker" disabled={template.isPending}>
          <legend>Stores / locations for template</legend>
          <input aria-label="Search template stores" placeholder="Search attached stores or cities" type="search" value={storeSearch} onChange={(event) => setStoreSearch(event.target.value)} />
          <div className="schedule-store-picker-actions">
            <button className="text-button" type="button" onClick={() => { setSelectedStores((current) => new Set([...current, ...visibleStores.map((item) => item.id)])); template.reset(); }}>Select visible</button>
            <button className="text-button" type="button" onClick={() => { setSelectedStores(new Set()); template.reset(); }}>Clear</button>
          </div>
          {locations.isPending ? <p role="status">Loading campaign stores…</p> : null}
          {locations.isError ? <p className="form-error" role="alert">{getApiErrorMessage(locations.error)} <button className="button secondary" type="button" onClick={() => { void locations.refetch(); }}>Retry stores</button></p> : null}
          <div className="schedule-store-options">{visibleStores.map((item) => <label key={item.id}>
            <input aria-label={`Include ${item.name} · ${item.city}`} type="checkbox" checked={selectedStores.has(item.id)} onChange={(event) => { setSelectedStores((current) => { const next = new Set(current); if (event.target.checked) next.add(item.id); else next.delete(item.id); return next; }); template.reset(); }} />
            <span>{item.name}<small>{item.city}</small></span>
          </label>)}</div>
          {locations.isSuccess && !visibleStores.length ? <p className="inline-help">No matching campaign stores.</p> : null}
          <small>{selectedStores.size} selected · maximum 50 stores and 200 template rows</small>
          {selectedStores.size > 50 ? <p className="form-error" role="alert">Select at most 50 stores per template.</p> : null}
        </fieldset>
        {!validDate ? <p className="inline-help">Choose a sales date within the campaign and not later than today at the selected stores.</p> : null}
        <button className="button secondary" disabled={template.isPending || !canDownload} onClick={() => template.mutate()} type="button"><Download size={16} /> {template.isPending ? 'Preparing template…' : 'Download CSV template'}</button>
        <p className="inline-help">Only selected stores are included, with every active sellable campaign SKU and its price for that store/date. Eligible BA emails, assignments and started shifts are prefilled; multiple BA shifts get separate product sets. No matching assignment means unassigned sales. Check the attribution before entering quantities; leave unsold rows blank. If a price is not configured, enter it before uploading. Template selections do not filter an uploaded file: review its rows below.</p>
        <label>Sales CSV<input accept=".csv,text/csv" type="file" onChange={(event) => { setFile(event.target.files?.[0] ?? null); resetPreview(); }} /></label>
        <label><span>Reason for manual import <span className="required-marker" aria-hidden="true">*</span></span><textarea required maxLength={500} rows={2} value={reason} onChange={(event) => { setReason(event.target.value); resetPreview(); }} /></label>
      </fieldset>
      {template.isError ? <p className="form-error" role="alert">{getApiErrorMessage(template.error)}</p> : null}
      {upload.isError ? <p className="form-error" role="alert">{getApiErrorMessage(upload.error)}</p> : null}
      {preview ? <section aria-label="Review CSV sales">
        <h3>Review CSV sales</h3>
        <p>{preview.valid_count} valid · {preview.invalid_count} invalid · {preview.skipped_count ?? 0} skipped · {selected.size} selected</p>
        <p className="inline-help">Only selected valid rows will be imported. Existing sales are never replaced.</p>
        <div className="data-table-wrap"><table className="data-table manual-sales-preview"><thead><tr>
          <th><input aria-label="Select all eligible sales rows" type="checkbox" disabled={upload.isPending || !validRows.length} checked={validRows.length > 0 && selected.size === validRows.length} onChange={(event) => setSelected(new Set(event.target.checked ? validRows.map((row) => row.row_number) : []))} /></th>
          <th>Row</th><th>Status</th><th>Location</th><th>Sales date</th><th>BA</th><th>Product</th><th>Quantity</th><th>Unit price (PKR)</th><th>Notes</th>
        </tr></thead><tbody>{preview.rows.map((row) => <tr key={row.row_number}>
          <td><input aria-label={`Select sales row ${row.row_number}`} type="checkbox" disabled={upload.isPending || row.status !== 'valid'} checked={selected.has(row.row_number)} onChange={(event) => setSelected((current) => { const next = new Set(current); if (event.target.checked) next.add(row.row_number); else next.delete(row.row_number); return next; })} /></td>
          <td>{row.row_number}</td>
          <td><strong className={`manual-import-row-status ${row.status}`}>{row.status}</strong><small>{row.detail}</small></td>
          <td>{row.values.location_name || row.values.location_id}</td><td>{row.values.sales_date}</td>
          <td>{row.values.ba_name || row.values.ba_email || 'Unassigned'}</td>
          <td>{row.values.product_name}<small>{row.values.product_sku}</small></td><td>{row.values.quantity}</td><td>{row.values.unit_price || '—'}</td><td>{row.values.notes}</td>
        </tr>)}</tbody></table></div>
      </section> : null}
      <div className="confirmation-actions">
        <button className="button secondary" disabled={upload.isPending} onClick={onClose} type="button">Cancel</button>
        <button className="button secondary" disabled={!canPreview} onClick={() => upload.mutate(false)} type="button">{upload.isPending ? 'Processing…' : preview ? 'Preview again' : 'Preview CSV'}</button>
        {preview ? <button className="button primary" disabled={upload.isPending || !selected.size} onClick={() => upload.mutate(true)} type="button"><Upload size={16} /> Import {selected.size} selected rows</button> : null}
      </div>
    </section>
  </div>;
}
